import { useEffect, useState, useSyncExternalStore } from "react";

import { useSessionStorage } from "~/hooks/use-session-storage";

import { onlineManager } from "@tanstack/react-query";

const subscribeToNetworkConnectivity = (callback: () => void) => {
  return onlineManager.subscribe(callback);
};

const getNetworkConnectivitySnapshot = () => {
  return onlineManager.isOnline();
};

// During SSR, assume online. On client (offline most likely), check navigator.onLine
const getNetworkConnectivityServerSnapshot = () =>
  typeof window === "undefined" || window.navigator.onLine;

export const useNetworkConnectivity = (
  options: {
    onOnline?: (isOnline: boolean) => void;
    onOffline?: (isOnline: boolean) => void;
  } = {},
) => {
  const isOnline = useSyncExternalStore(
    subscribeToNetworkConnectivity,
    getNetworkConnectivitySnapshot,
    getNetworkConnectivityServerSnapshot,
  );

  const [offlineSince, setOfflineSince] = useSessionStorage<{
    dateTimeString: string | null;
  } | null>("use-network-connectivity-offline-since", null);

  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (isOnline) {
      setOfflineSince({ dateTimeString: null });
    } else if (offlineSince && offlineSince.dateTimeString === null) {
      setOfflineSince({ dateTimeString: new Date().toISOString() });
    }
  }, [isOnline, setOfflineSince, offlineSince]);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    if (isOnline) {
      options.onOnline?.(true);
    } else {
      options.onOffline?.(false);
    }
  }, [isOnline, hasHydrated, options]);

  // Return true until hydrated, then return actual status
  return {
    isOnline: hasHydrated ? isOnline : true,
    offlineSince: offlineSince?.dateTimeString
      ? new Date(offlineSince.dateTimeString)
      : null,
  };
};
